// ─────────────────────────────────────────────
// analytics/eventTracker.js  (v2 — taxonomy-driven)
// AtlasRouteEQ™ — Behavioral Event Logger
//
// Single intake point for all behavioral signals.
// Enforces the contract defined in eventTaxonomy.js.
//
// NEW CODE — Commercial Use Review:
//   - No third-party libraries
//   - Uses @supabase/supabase-js (MIT) ✅
//   - All logic is original IP ✅
//
// License: MIT (your original IP)
// ─────────────────────────────────────────────

import { supabase } from "../services/supabaseService";
import { EVENTS, REQUIRED_EVENT_FIELDS } from "./eventTaxonomy";
import { getSessionId, updateSessionMeta, recordStepDuration } from "./sessionTracker";

export { EVENTS };

/**
 * Log a behavioral event.
 * This is the ONLY function pages should call to emit events.
 *
 * @param {string} eventType   - One of EVENTS constants from eventTaxonomy.js
 * @param {string|null} userId - Supabase user ID
 * @param {object} metadata    - Optional metadata (see STANDARD_METADATA_FIELDS)
 */
export async function trackEvent(eventType, userId = null, metadata = {}) {
  // Respect analytics opt-out preference
  try { if (localStorage.getItem("loaddash_analytics_optout") === "true") return; } catch {}

  if (!Object.values(EVENTS).includes(eventType)) {
    console.warn(`[AtlasRouteEQ] Unknown event type: "${eventType}". Use EVENTS constants.`);
  }

  const payload = {
    event_type:  eventType,
    user_id:     userId,
    session_id:  getSessionId(),
    occurred_at: new Date().toISOString(),
    metadata,
  };

  // Validate required fields
  for (const field of REQUIRED_EVENT_FIELDS) {
    if (payload[field] === undefined) {
      console.warn(`[AtlasRouteEQ] Missing required field "${field}" on event "${eventType}"`);
    }
  }

  try {
    const { error } = await supabase.from("behavioral_events").insert([payload]);
    if (error) {
      console.debug("[ANALYTICS_DEBUG] suppressed_nonfatal_event_failure", {
        event: eventType,
        table: "behavioral_events",
        code: error.code || null,
        reason: error.message?.slice(0, 120) || "unknown_error",
      });
    }
  } catch (e) {
    console.debug("[ANALYTICS_DEBUG] suppressed_nonfatal_event_failure", {
      event: eventType,
      table: "behavioral_events",
      reason: e?.message?.slice(0, 120) || "network_error",
    });
  }
}

/**
 * Enter a booking step. Logs STEP_VIEWED and starts a timer.
 * Returns a stepRef object with methods for explicit lifecycle events.
 * Pages must call completeStep/abandonStep/backtrackStep explicitly.
 * Cleanup only logs abandonment if no explicit exit was recorded.
 */
export function enterStep(step, userId = null, metadata = {}) {
  const startTime = Date.now();
  let exited = false;
  trackEvent(EVENTS.BOOKING_STEP_VIEWED, userId, { step, ...metadata });

  function getDuration() {
    const d = Date.now() - startTime;
    recordStepDuration(step, d);
    return d;
  }

  return {
    completeStep() {
      if (exited) return;
      exited = true;
      const durationMs = getDuration();
      trackEvent(EVENTS.BOOKING_STEP_COMPLETED, userId, { step, duration_ms: durationMs, hesitation: durationMs > 15000, ...metadata });
    },
    abandonStep() {
      if (exited) return;
      exited = true;
      const durationMs = getDuration();
      trackEvent(EVENTS.BOOKING_STEP_ABANDONED, userId, { step, duration_ms: durationMs, ...metadata });
    },
    backtrackStep() {
      if (exited) return;
      exited = true;
      const durationMs = getDuration();
      trackEvent(EVENTS.BOOKING_BACKTRACKED, userId, { step, duration_ms: durationMs, ...metadata });
    },
    /** Cleanup for useEffect — logs abandonment only if no explicit exit was called */
    cleanup() {
      if (!exited) {
        exited = true;
        const durationMs = getDuration();
        trackEvent(EVENTS.BOOKING_STEP_ABANDONED, userId, { step, duration_ms: durationMs, ...metadata });
      }
    },
  };
}

/** @deprecated Use enterStep instead. Kept for backward compatibility. */
export function trackStep(step, userId = null, metadata = {}) {
  const ref = enterStep(step, userId, metadata);
  return ref.cleanup;
}

/**
 * Track a page view and update session page count.
 * @param {string} page
 * @param {string|null} userId
 */
export function trackPageView(page, userId = null) {
  try {
    const meta = JSON.parse(sessionStorage.getItem("atlasrouteeq_session_meta") || "{}");
    updateSessionMeta({ last_page: page, page_views: (meta.page_views || 0) + 1 });
  } catch (e) {}
  trackEvent(EVENTS.PAGE_VIEWED, userId, { page });
}
